import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateDraftItemsOptions,
  BulkCreateDraftItemsResponse,
  BulkDeleteDraftItemsResponse,
  BulkUpdateDraftItemsOptions,
  BulkUpdateDraftItemsResponse,
  CreateDraftItemOptions,
  DraftItem,
  DraftItemQuery,
  DraftItemsQueryBuilder,
  GetDeletedDraftItemResponse,
  ListDeletedDraftItemsOptions,
  ListDeletedDraftItemsResponse,
  MaskedDraftItem,
  MoveDraftItemsApplicationErrors,
  MoveDraftItemsOptions,
  QueryDraftItemsResponse,
  RestoreDraftItemFromTrashBinResponse,
  SortByFieldSorting,
  SortDraftItemsByApplicationErrors,
  SortDraftItemsByOptions,
  SortDraftItemsByResponse,
  UpdateDraftItem,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  bulkCreateDraftItems as universalBulkCreateDraftItems,
  bulkDeleteDraftItems as universalBulkDeleteDraftItems,
  bulkUpdateDraftItems as universalBulkUpdateDraftItems,
  createDraftItem as universalCreateDraftItem,
  deleteDraftItem as universalDeleteDraftItem,
  getDeletedDraftItem as universalGetDeletedDraftItem,
  getDraftItem as universalGetDraftItem,
  listDeletedDraftItems as universalListDeletedDraftItems,
  moveDraftItems as universalMoveDraftItems,
  queryDraftItems as universalQueryDraftItems,
  removeDraftItemFromTrashBin as universalRemoveDraftItemFromTrashBin,
  restoreDraftItemFromTrashBin as universalRestoreDraftItemFromTrashBin,
  sortDraftItemsBy as universalSortDraftItemsBy,
  typedQueryDraftItems as universalTypedQueryDraftItems,
  updateDraftItem as universalUpdateDraftItem,
  updateExtendedFields as universalUpdateExtendedFields,
} from './fastgallery-draftitem-v1-draft-item-draft-items.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/fast-gallery' };

export function createDraftItem(
  httpClient: HttpClient
): CreateDraftItemSignature {
  return (draftItem: DraftItem, options?: CreateDraftItemOptions) =>
    universalCreateDraftItem(
      draftItem,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDraftItemSignature {
  /**
   * Creates a DraftFastGalleryItem.
   *
   * The request body must include lorem and business address. Ipsum is optional.
   * @param - DraftFastGalleryItem to be created.
   * @returns The created DraftFastGalleryItem.
   */
  (draftItem: DraftItem, options?: CreateDraftItemOptions): Promise<
    NonNullablePaths<
      DraftItem,
      | `media.imageOptions.focalPoint.x`
      | `media.imageOptions.focalPoint.y`
      | `media.type`,
      5
    >
  >;
}

export function getDraftItem(httpClient: HttpClient): GetDraftItemSignature {
  return (draftItemId: string) =>
    universalGetDraftItem(
      draftItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDraftItemSignature {
  /**
   * Retrieves a DraftFastGalleryItem.
   * @param - ID of the DraftFastGalleryItem to retrieve.
   * @returns The requested DraftFastGalleryItem.
   */
  (draftItemId: string): Promise<
    NonNullablePaths<
      DraftItem,
      | `media.imageOptions.focalPoint.x`
      | `media.imageOptions.focalPoint.y`
      | `media.type`,
      5
    >
  >;
}

export function updateDraftItem(
  httpClient: HttpClient
): UpdateDraftItemSignature {
  return (
    _id: string,
    draftItem: NonNullablePaths<UpdateDraftItem, `revision`, 2>
  ) =>
    universalUpdateDraftItem(
      _id,
      draftItem,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDraftItemSignature {
  /**
   * Updates a DraftFastGalleryItem.
   * @param - Item ID
   * @returns Updated DraftFastGalleryItem.
   */
  (
    _id: string,
    draftItem: NonNullablePaths<UpdateDraftItem, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      DraftItem,
      | `media.imageOptions.focalPoint.x`
      | `media.imageOptions.focalPoint.y`
      | `media.type`,
      5
    >
  >;
}

export function deleteDraftItem(
  httpClient: HttpClient
): DeleteDraftItemSignature {
  return (draftItemId: string) =>
    universalDeleteDraftItem(
      draftItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteDraftItemSignature {
  /**
   * Deletes a DraftFastGalleryItem.
   * @param - Id of the DraftFastGalleryItem to delete.
   */
  (draftItemId: string): Promise<void>;
}

export function queryDraftItems(
  httpClient: HttpClient
): QueryDraftItemsSignature {
  return () =>
    universalQueryDraftItems(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryDraftItemsSignature {
  /**
   * Creates a query to retrieve a list of draft items.
   *
   * The `queryDraftItems()` method builds a query to retrieve a list of draft items and returns a `DraftItemsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `DraftItemsQueryBuilder` methods onto the query. `DraftItemsQueryBuilder` methods enable you to filter, sort, and control the results that `queryDraftItems()` returns.
   */
  (): DraftItemsQueryBuilder;
}

export function typedQueryDraftItems(
  httpClient: HttpClient
): TypedQueryDraftItemsSignature {
  return (query: DraftItemQuery) =>
    universalTypedQueryDraftItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryDraftItemsSignature {
  /** */
  (query: DraftItemQuery): Promise<
    NonNullablePaths<
      QueryDraftItemsResponse,
      `draftItems` | `draftItems.${number}.media.type`,
      5
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a DraftFastGalleryItem without incrementing revision
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `draftItem.media.imageOptions.focalPoint.x`
      | `draftItem.media.imageOptions.focalPoint.y`
      | `draftItem.media.type`,
      6
    >
  >;
}

export function bulkCreateDraftItems(
  httpClient: HttpClient
): BulkCreateDraftItemsSignature {
  return (draftItems: DraftItem[], options?: BulkCreateDraftItemsOptions) =>
    universalBulkCreateDraftItems(
      draftItems,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateDraftItemsSignature {
  /**
   * Create multiple DraftFastGalleryItems in a single request. Works synchronously.
   * @param - List of DraftFastGalleryItems to be created
   */
  (draftItems: DraftItem[], options?: BulkCreateDraftItemsOptions): Promise<
    NonNullablePaths<
      BulkCreateDraftItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.media.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateDraftItems(
  httpClient: HttpClient
): BulkUpdateDraftItemsSignature {
  return (
    draftItems: NonNullablePaths<MaskedDraftItem, `draftItem.revision`, 3>[],
    options?: BulkUpdateDraftItemsOptions
  ) =>
    universalBulkUpdateDraftItems(
      draftItems,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateDraftItemsSignature {
  /**
   * Update multiple DraftFastGalleryItems in a single request. Works synchronously.
   * @param - List of DraftFastGalleryItems to be updated.
   */
  (
    draftItems: NonNullablePaths<MaskedDraftItem, `draftItem.revision`, 3>[],
    options?: BulkUpdateDraftItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateDraftItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.media.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteDraftItems(
  httpClient: HttpClient
): BulkDeleteDraftItemsSignature {
  return (draftItemIds: string[]) =>
    universalBulkDeleteDraftItems(
      draftItemIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteDraftItemsSignature {
  /**
   * Delete multiple DraftFastGalleryItems in a single request. Works synchronously.
   * @param - DraftFastGalleryItem ids to be deleted
   */
  (draftItemIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteDraftItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function removeDraftItemFromTrashBin(
  httpClient: HttpClient
): RemoveDraftItemFromTrashBinSignature {
  return (draftItemId: string) =>
    universalRemoveDraftItemFromTrashBin(
      draftItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveDraftItemFromTrashBinSignature {
  /**
   * Removes a DraftItem from TrashBin, permanently deleting it.
   * @param - ID of the DraftItem to remove from TrashBin
   */
  (draftItemId: string): Promise<void>;
}

export function restoreDraftItemFromTrashBin(
  httpClient: HttpClient
): RestoreDraftItemFromTrashBinSignature {
  return (draftItemId: string) =>
    universalRestoreDraftItemFromTrashBin(
      draftItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface RestoreDraftItemFromTrashBinSignature {
  /**
   * Restores a DraftItem from TrashBin. Increments the revision.
   * @param - ID of the DraftItem to restore from TrashBin
   */
  (draftItemId: string): Promise<
    NonNullablePaths<
      RestoreDraftItemFromTrashBinResponse,
      | `draftItem.media.imageOptions.focalPoint.x`
      | `draftItem.media.imageOptions.focalPoint.y`
      | `draftItem.media.type`,
      6
    >
  >;
}

export function getDeletedDraftItem(
  httpClient: HttpClient
): GetDeletedDraftItemSignature {
  return (draftItemId: string) =>
    universalGetDeletedDraftItem(
      draftItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDeletedDraftItemSignature {
  /**
   * Retrieves a DraftItem from TrashBin. (DraftItem is kept in TrashBin)
   * @param - ID of the DraftItem to retrieve from TrashBin
   */
  (draftItemId: string): Promise<
    NonNullablePaths<
      GetDeletedDraftItemResponse,
      | `draftItem.media.imageOptions.focalPoint.x`
      | `draftItem.media.imageOptions.focalPoint.y`
      | `draftItem.media.type`,
      6
    >
  >;
}

export function listDeletedDraftItems(
  httpClient: HttpClient
): ListDeletedDraftItemsSignature {
  return (options?: ListDeletedDraftItemsOptions) =>
    universalListDeletedDraftItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDeletedDraftItemsSignature {
  /**
   * Retrieves a list of DraftItems from TrashBin.
   * If 'draft_item_ids' is provided returns only the requested DraftItems.
   * If query is provided, returns DraftItems that match the query.
   * If both provided, 'draft_item_ids' supersedes the query.
   */
  (options?: ListDeletedDraftItemsOptions): Promise<
    NonNullablePaths<
      ListDeletedDraftItemsResponse,
      `draftItems` | `draftItems.${number}.media.type`,
      5
    >
  >;
}

export function moveDraftItems(
  httpClient: HttpClient
): MoveDraftItemsSignature {
  return (itemIds: string[], options?: MoveDraftItemsOptions) =>
    universalMoveDraftItems(
      itemIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface MoveDraftItemsSignature {
  /**
   * Move Draft Items to be after another Draft Item.
   * If beforeItemId is not provided, the DraftItem will be positioned first.
   * @param - DraftItem ids to be moved after `after_item_id`, if not provided, draftItems given will be positioned first
   */
  (itemIds: string[], options?: MoveDraftItemsOptions): Promise<
    void & {
      __applicationErrorsType?: MoveDraftItemsApplicationErrors;
    }
  >;
}

export function sortDraftItemsBy(
  httpClient: HttpClient
): SortDraftItemsBySignature {
  return (
    sorting: NonNullablePaths<SortByFieldSorting, `fieldName`, 2>,
    options: NonNullablePaths<SortDraftItemsByOptions, `draftGalleryId`, 2>
  ) =>
    universalSortDraftItemsBy(
      sorting,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SortDraftItemsBySignature {
  /**
   * Sort Draft Items by field name and order.
   * @param - DraftItem ids to be sorted by field `sorting.field_name`, in direction `sorting.order`
   */
  (
    sorting: NonNullablePaths<SortByFieldSorting, `fieldName`, 2>,
    options: NonNullablePaths<SortDraftItemsByOptions, `draftGalleryId`, 2>
  ): Promise<
    NonNullablePaths<SortDraftItemsByResponse, `jobId`, 2> & {
      __applicationErrorsType?: SortDraftItemsByApplicationErrors;
    }
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddressLink,
  AnchorLink,
  ApplicationError,
  BulkActionMetadata,
  BulkCreateDraftItemsOptions,
  BulkCreateDraftItemsRequest,
  BulkCreateDraftItemsResponse,
  BulkDeleteDraftItemsRequest,
  BulkDeleteDraftItemsResponse,
  BulkDeleteDraftItemsResponseBulkDraftItemResult,
  BulkDraftItemResult,
  BulkUpdateDraftItemsOptions,
  BulkUpdateDraftItemsRequest,
  BulkUpdateDraftItemsResponse,
  BulkUpdateDraftItemsResponseBulkDraftItemResult,
  CreateDraftItemOptions,
  CreateDraftItemRequest,
  CreateDraftItemResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteDraftItemRequest,
  DeleteDraftItemResponse,
  DocumentLink,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftItem,
  DraftItemNotFoundError,
  DraftItemNotInGalleryError,
  DraftItemQuerySpec,
  DraftItemSortUnsupportedError,
  DraftItemsQueryBuilder,
  DraftItemsQueryResult,
  DynamicPageLink,
  EmailLink,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  ExternalLink,
  FocalPoint,
  GetDeletedDraftItemRequest,
  GetDeletedDraftItemResponse,
  GetDraftItemRequest,
  GetDraftItemResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ItemMetadata,
  Link,
  LinkRel,
  LinkType,
  ListDeletedDraftItemsOptions,
  ListDeletedDraftItemsRequest,
  ListDeletedDraftItemsResponse,
  MaskedDraftItem,
  MediaItem,
  MediaItemMediaOneOf,
  MessageEnvelope,
  MoveDraftItemsOptions,
  MoveDraftItemsRequest,
  MoveDraftItemsResponse,
  PageLink,
  PhoneLink,
  Point,
  QueryDraftItemsRequest,
  QueryDraftItemsResponse,
  RemoveDraftItemFromTrashBinRequest,
  RemoveDraftItemFromTrashBinResponse,
  ReorderedDraftItem,
  RestoreDraftItemFromTrashBinRequest,
  RestoreDraftItemFromTrashBinResponse,
  RestoreInfo,
  SortByFieldSorting,
  SortDraftItemsByOptions,
  SortDraftItemsByRequest,
  SortDraftItemsByResponse,
  SortOrder,
  SortableFieldNames,
  Sorting,
  TpaPageLink,
  Type,
  UnsharpMasking,
  UpdateDraftItem,
  UpdateDraftItemRequest,
  UpdateDraftItemResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  WebhookIdentityType,
  WhatsAppLink,
  WixLink,
  WixLinkLinkOneOf,
  utils,
} from './fastgallery-draftitem-v1-draft-item-draft-items.universal.js';
